/**
 * Favorites Panel - Feature A
 * Quick-access saved product configurations
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function FavoritesPanel({ onAddToCart }) {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/favorites/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Load favorites error:', error);
    }
  };

  const useFavorite = async (favorite) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/favorites/${favorite.favorite_id}/use`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        onAddToCart && onAddToCart(data);
        toast.success('Favorit zum Warenkorb hinzugefügt!');
        loadFavorites(); // Refresh usage count
      }
    } catch (error) {
      toast.error('Fehler beim Hinzufügen');
    }
  };

  const deleteFavorite = async (favoriteId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/favorites/${favoriteId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Favorit gelöscht');
        loadFavorites();
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (favorites.length === 0) return null;

  return (
    <div className="mb-4 p-4 bg-[#111C32]/60 rounded-xl border border-amber-500/30">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
        <span className="text-sm font-semibold text-amber-400 uppercase tracking-wide">Favoriten</span>
        <Badge variant="outline" className="text-xs text-amber-400 border-amber-400/50">
          {favorites.length}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {favorites.slice(0, 8).map((fav) => (
          <div key={fav.favorite_id} className="relative group">
            <Button
              onClick={() => useFavorite(fav)}
              variant="outline"
              className="w-full h-auto min-h-[60px] p-3 flex flex-col items-start text-left
                       border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/15 hover:border-amber-400
                       transition-all active:scale-[0.98]"
            >
              <span className="text-xs text-amber-400 font-semibold line-clamp-2">
                {fav.display_name}
              </span>
              <span className="text-xs text-slate-400 mt-1">
                {fav.total_price?.toFixed(2)}€
              </span>
              {fav.usage_count > 0 && (
                <Badge variant="outline" className="mt-1 text-xs border-amber-600 text-amber-400">
                  {fav.usage_count}x
                </Badge>
              )}
            </Button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteFavorite(fav.favorite_id);
              }}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500/90 hover:bg-red-600
                       flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
